import type { Camera } from './camera';

/**
 * Mission control's view, remembered per browser history entry (react-router's
 * `location.key`) in sessionStorage, so browser Back/Forward returns to the same
 * selection and camera, while a fresh visit (a new key) starts fitted.
 */
export interface SavedView {
  selectedId: string | null;
  /** In the planets' home frame (see Universe's onViewChange); null = the fitted view. */
  camera: Camera | null;
}

export const VIEW_KEY_PREFIX = 'aiai.mc.view:';
/** At most this many entries are kept; the least recently written go first. */
export const MAX_SAVED_VIEWS = 20;

const isId = (v: unknown): v is string | null => v === null || typeof v === 'string';
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function parse(raw: string | null): (SavedView & { at: number }) | null {
  if (!raw) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  if (!isId(o.selectedId)) return null;
  let camera: Camera | null = null;
  if (o.camera !== null && o.camera !== undefined) {
    const c = o.camera as Record<string, unknown>;
    if (typeof c !== 'object' || !isNum(c.x) || !isNum(c.y) || !isNum(c.scale) || c.scale <= 0) return null;
    camera = { x: c.x, y: c.y, scale: c.scale };
  }
  return { selectedId: o.selectedId, camera, at: isNum(o.at) ? o.at : 0 };
}

function store(storage?: Storage): Storage | null {
  try {
    return storage ?? globalThis.sessionStorage ?? null;
  } catch {
    return null; // touching sessionStorage itself can throw when site data is blocked
  }
}

/** The view saved for this history entry, or null (none, unreadable, or corrupt). */
export function readView(key: string, storage?: Storage): SavedView | null {
  try {
    const s = store(storage);
    const v = s ? parse(s.getItem(VIEW_KEY_PREFIX + key)) : null;
    return v ? { selectedId: v.selectedId, camera: v.camera } : null;
  } catch {
    return null;
  }
}

/** Saves the view for this history entry and prunes to the newest MAX_SAVED_VIEWS. Never throws. */
export function writeView(key: string, view: SavedView, opts: { storage?: Storage; now?: number } = {}): void {
  try {
    const s = store(opts.storage);
    if (!s) return;
    const at = opts.now ?? Date.now();
    s.setItem(VIEW_KEY_PREFIX + key, JSON.stringify({ ...view, at }));
    const entries: { k: string; at: number }[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (!k || !k.startsWith(VIEW_KEY_PREFIX) || k === VIEW_KEY_PREFIX + key) continue;
      const v = parse(s.getItem(k));
      // Corrupt entries sort first (at -1), so they are the first to go.
      entries.push({ k, at: v ? v.at : -1 });
    }
    const excess = entries.length + 1 - MAX_SAVED_VIEWS;
    if (excess <= 0) return;
    entries.sort((a, b) => a.at - b.at);
    for (const e of entries.slice(0, excess)) s.removeItem(e.k);
  } catch {
    // storage full or blocked: memory is best-effort
  }
}

let firstMountClaimed = false;

/**
 * True when this mount must ignore (and drop) the saved view: the document's first
 * Mission control mount, on react-router's "default" key, after a fresh navigation (a
 * typed URL or a bookmark). Every load without history.state gets the "default" key, so
 * without this a typed URL would restore an old view. Later mounts on "default" (Back to
 * the first entry) and reload / back_forward loads still restore it.
 */
export function skipFreshLoadView(key: string, perf?: Performance): boolean {
  if (firstMountClaimed) return false;
  firstMountClaimed = true;
  if (key !== 'default') return false;
  let fresh = false;
  try {
    const nav = (perf ?? globalThis.performance).getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    fresh = nav?.type === 'navigate';
  } catch {
    return false;
  }
  if (fresh) {
    try {
      store()?.removeItem(VIEW_KEY_PREFIX + key);
    } catch {
      // nothing to drop
    }
  }
  return fresh;
}

/** Tests only: start over as a freshly loaded document. */
export function resetFirstMount(): void {
  firstMountClaimed = false;
}
